import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import PaymentOnReceiptFields from './PaymentOnReceiptFields';
import {
  checkPaymentDraft,
  describePaymentAnswer,
  draftFromStored,
  type PaymentDraft,
} from './paymentOnReceiptForm';
import { setVendorInvoicePaymentOnReceipt, type VendorInvoice } from '../../lib/vendorInvoiceApi';

// The review-screen home of the answer to "was this invoice already paid?" (D49).
//
// Until the invoice is confirmed the answer can be given or corrected here (a part
// payment's amount is typed here, once the invoice has been read). After Confirm &
// sync the bill exists and the payment is a payment like any other, so it is shown
// and not editable; corrections are made on the bill in Accounts.

export interface PaymentOnReceiptCardProps {
  invoice: Pick<VendorInvoice, '_id' | 'paymentOnReceipt' | 'inventoryConfirmedAt'>;
  /** The page reloads the invoice after a save, so the card never holds a stale copy. */
  onSaved: () => void | Promise<void>;
  readOnly?: boolean;
}

const PaymentOnReceiptCard: React.FC<PaymentOnReceiptCardProps> = ({ invoice, onSaved, readOnly = false }) => {
  const stored = invoice.paymentOnReceipt || null;
  const confirmed = Boolean(invoice.inventoryConfirmedAt);
  const [draft, setDraft] = useState<PaymentDraft>(() => draftFromStored(stored));
  const [paidDateValid, setPaidDateValid] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // A save elsewhere (or a reload) replaces what the fields show.
  useEffect(() => {
    setDraft(draftFromStored(stored));
    setMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice._id, stored?.status, stored?.amount, stored?.paidDate, stored?.method, stored?.reference]);

  if (confirmed || readOnly) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="payment-on-receipt-locked">
        <span className="font-semibold text-slate-800">Payment on receipt: </span>
        {describePaymentAnswer(stored)}
        {confirmed ? <span className="text-slate-500"> — recorded on its bill in Accounts.</span> : null}
      </div>
    );
  }

  const unanswered = !stored?.status;
  const needsAmount = stored?.status === 'part_paid' && !(typeof stored.amount === 'number' && stored.amount > 0);

  async function save() {
    const checked = checkPaymentDraft(draft, { includeAmount: true, dateRefused: !paidDateValid });
    if (!checked.ok) {
      setMessage(checked.message);
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await setVendorInvoicePaymentOnReceipt(invoice._id, checked.input);
      await onSaved();
    } catch (e: any) {
      setMessage(e?.message || 'Could not save the payment answer.');
    } finally {
      setBusy(false);
    }
  }

  // Nothing to save while the fields still show the stored answer. The button
  // stayed lit after a save, reading as if the answer had not been kept
  // (browser QA, 2026-09-25).
  const saved = draftFromStored(stored);
  const dirty = (Object.keys(saved) as (keyof PaymentDraft)[]).some((k) => draft[k] !== saved[k]);

  const tone = unanswered || needsAmount ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white';

  return (
    <div className={`rounded-xl border px-3 py-3 space-y-2 ${tone}`} data-testid="payment-on-receipt-card">
      {unanswered ? (
        <div role="alert" className="text-xs font-semibold text-amber-800">
          Not answered yet. Confirm &amp; sync needs to know whether this invoice was already paid.
        </div>
      ) : needsAmount ? (
        <div role="alert" className="text-xs font-semibold text-amber-800">
          Part paid: enter the amount already paid. Confirm &amp; sync waits for it.
        </div>
      ) : (
        <div className="text-xs text-slate-600">
          <span className="font-semibold text-slate-800">Payment on receipt: </span>
          {describePaymentAnswer(stored)}
        </div>
      )}

      <PaymentOnReceiptFields draft={draft} onChange={setDraft} showAmount disabled={busy} idPrefix={`review-${invoice._id}`} onDateValidityChange={setPaidDateValid} />

      {message ? (
        <div role="alert" className="text-xs text-rose-700 whitespace-pre-line">
          {message}
        </div>
      ) : null}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy || !draft.status || !dirty}
          title={!dirty && draft.status ? 'This answer is saved' : undefined}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary-600 bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy && <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />}
          {busy ? 'Saving…' : 'Save payment answer'}
        </button>
      </div>
    </div>
  );
};

export default PaymentOnReceiptCard;
